import { Supplier } from '../../core/utils';
import { bubbleElement, equals, isNonEmptyArray } from '../../core/utils/his-array.utils';

/**
 * Helper class for moving table columns within the column order.
 */
export class TableColumnMover {
    private colOrderTmp: string[];
    private orderedVisibleColumnsTmp: string[];

    /**
     * Creates an instance of TableColumnMover.
     * @param colOrder A supplier function that provides the current order of _all_ columns, regardless of visibility. (Accepts raw signals)
     * @param orderedVisibleColumns A supplier function that provides the current ordered list of visible columns. (Accepts raw signals)
     */
    constructor(
        private readonly colOrder: Supplier<string[]>,
        private readonly orderedVisibleColumns?: Supplier<string[]>
    ) { }
    
    /**
     * Moves the specified column one position to the left among the visible columns.
     * 
     * If the column is already at the edge (first or last visible column), no change is made.\
     * If the column is not visible itself or not found in the order, no change is made.
     * @param columnId the ID of the column to move
     * @returns the updated column order after the move operation, or the original order if the move fails
     */
    public moveLeft( columnId: string ) {
        this.updateCache();

        const idxTo = this.findIndexOfNextVisibleColumn( columnId, 'left' );
        return this.doMove( columnId, idxTo );
    }

    /**
     * Moves the specified column one position to the right among the visible columns.
     * 
     * If the column is already at the edge (first or last visible column), no change is made.\
     * If the column is not visible itself or not found in the order, no change is made.
     * @param columnId the ID of the column to move
     * @returns the updated column order after the move operation, or the original order if the move fails
     */
    public moveRight( columnId: string ) {
        this.updateCache();

        const idxTo = this.findIndexOfNextVisibleColumn( columnId, 'right' );
        return this.doMove( columnId, idxTo );
    }

    /**
     * Moves the specified column to the target index in the column order, regardless of visibility.
     * @param columnId the ID of the column to move
     * @param idxTo the target index of the column to move
     * @returns the updated column order after the move operation, or the original order if the move fails
     */
    public moveToIndex( columnId: string, idxTo: number ) {
        this.updateCache();

        return this.doMove( columnId, idxTo );
    }

    /** Caches the current values for the execution. */
    private updateCache() {
        this.colOrderTmp = [...this.colOrder()];
        this.orderedVisibleColumnsTmp = this.orderedVisibleColumns ? [...this.orderedVisibleColumns()] : [];
    }

    /** Returns the current index of the specified column in the column order. */
    private getIndex( columnId: string ) {
        return this.colOrderTmp.indexOf( columnId );
    }

    /** Returns the current index of the specified column in the ordered visible columns. */
    private getVisibleIndex( columnId: string ) {
        return this.orderedVisibleColumnsTmp.indexOf( columnId );
    }

    /**
     * Finds the index of the next visible column in the specified direction ('left' or 'right').
     * Returns -1 if there is no next visible column or the original column is not visible itself.
     */
    private findIndexOfNextVisibleColumn( columnId: string, direction: 'left' | 'right' ) {
        const ownVisibleIdx = this.getVisibleIndex( columnId );
        if ( ownVisibleIdx < 0 ) {
            return -1;
        }
        const nextVisibleIdx = direction === 'left' ? ownVisibleIdx - 1 : ownVisibleIdx + 1;
        if ( nextVisibleIdx < 0 || nextVisibleIdx >= this.orderedVisibleColumnsTmp.length ) {
            return -1;
        }
        return this.getIndex( this.orderedVisibleColumnsTmp[nextVisibleIdx] );
    }

    /** Tries to perform the actual move operation. Returns the original order if the move fails for any reason. */
    private doMove( columnId: string, idxTo: number ) {
        const idxFrom = this.getIndex( columnId );
        try {
            return bubbleElement( [...this.colOrderTmp], idxFrom, idxTo );
        } catch {
            return this.colOrderTmp;
        }
    }
}

/**
 * Normalizes the column order by comparing it with the natural order. If the column order is non-empty and different from the natural order,
 * it is returned; otherwise, `null` is returned.
 */
export function normalizeColOrder( colOrder: string[] | null, naturalOrder: string[] ): string[] | null {
    if ( isNonEmptyArray( colOrder ) && !equals( colOrder, naturalOrder ) ) {
        return colOrder;
    }
    return null;
}

/**
 * Calculates the current column order based on the natural order, the configuration order, and the locked columns.\
 * If the configuration order is empty or null, the natural order is returned. If there are missing columns in the configuration order,
 * they are appended to the end of the order, taking into account the number of locked columns at the end.
 * @param naturalOrder The natural order of the columns
 * @param configOrder The configured column order
 * @returns The calculated current column order
 */
export function calculateColumnOrder( naturalOrder: string[], configOrder: string[] | null ): string[] {
    if ( !configOrder || configOrder.length === 0 ) {
        return naturalOrder;
    }

    const naturalSet = new Set( naturalOrder );
    const known = [...new Set( configOrder )].filter( id => naturalSet.has( id ) ); // drops stale ids and duplicates
    const knownSet = new Set( known );
    const missing = naturalOrder.filter( id => !knownSet.has( id ) );
    return missing.length === 0 ? known : [...known, ...missing];
}
